import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AuthContext, IntlContext } from "context";
import { useNavigate, useParams } from "react-router";
import { io } from "socket.io-client";
import { fetchAllConversations } from "hooks/thread";
import { countUnreadConversations } from "helpers/messaging/countUnreadConversations";
import ListMessage from "./List";
import Discussion from "./Discussion";
import { Loader } from "ui/Loader";
import { buildRoute } from "routes";

const SOCKET_URL: string = process.env.REACT_APP_SOCKET_URL || "";

const socket = io(SOCKET_URL, {
    autoConnect: false,
    withCredentials: true,
});

const MessagesBooking: React.FC = () => {
    const { account } = useContext(AuthContext);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const { service_id: serviceId } = useParams() as { service_id: string };
    const { client_id: clientId } = useParams() as { client_id: string };
    const concatServiceClientId = serviceId + clientId;
    const intl = useContext(IntlContext);
    const navigate = useNavigate();

    const { data, isLoading, refetch } = useQuery({
        queryKey: ["all-conversations"],
        queryFn: fetchAllConversations,
    });
    const refreshData = useCallback(() => {
        void refetch();
    }, [refetch]);

    useEffect(() => {
        socket.connect();
        setIsConnected(true);

        const handleConnect = () => {
            if (account?.user.id) {
                socket.emit("register_presence", { user_id: account.user.id });
            }
        };

        socket.on("connect", handleConnect);
        if (socket.connected && account?.user.id) {
            socket.emit("register_presence", { user_id: account.user.id });
        }

        return () => {
            socket.off("connect", handleConnect);
            socket.disconnect();
        };
    }, [account?.user.id]);

    useEffect(() => {
        const isDesktop = window.innerWidth >= 768;
        if (isDesktop && !isLoading && data && data.length > 0 && !serviceId && !clientId) {
            navigate(buildRoute.messaging(data[0].service_id, data[0].client_id));
        }
    }, [clientId, data, isLoading, navigate, serviceId]);

    const unreadConversationCount = useMemo(() => countUnreadConversations(data), [data]);

    if (isLoading || !data) {
        return <Loader />;
    }

    const isConversationActive = !!serviceId && !!clientId;

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="flex gap-4 mx-auto w-full max-w-[1400px] px-4 md:px-12 py-4 md:py-8 md:gap-6 h-[calc(100svh-100px)] md:h-[calc(100vh-140px)]">
                <div
                    className={`${isConversationActive ? "hidden md:flex" : "flex"} w-full md:w-[454px] flex-shrink-0 flex-col bg-white border border-[#f1f5f9] rounded-4xl shadow-card overflow-hidden`}
                >
                    <div className="flex items-center gap-3 px-6 py-6 border-b border-[#f1f5f9] bg-white">
                        <h2 className="text-2xl font-black text-[#1a1c2d]">
                            {intl.formatMessage("booking.messages.label.title")}
                        </h2>
                        {unreadConversationCount > 0 && (
                            <span className="ml-auto text-xs font-semibold bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full">
                                {unreadConversationCount}
                            </span>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        <ListMessage data={data} refreshDataConversation={refreshData} isLoading={false} />
                    </div>
                </div>

                <div
                    className={`${!isConversationActive ? "hidden md:flex" : "flex"} flex-1 flex-col bg-white border border-[#f1f5f9] rounded-4xl shadow-card overflow-hidden`}
                >
                    {isConnected && serviceId && clientId ? (
                        <Discussion key={concatServiceClientId} socket={socket} refreshDataConversation={refreshData} />
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    className="w-8 h-8 text-muted-foreground"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
                                    />
                                </svg>
                            </div>
                            <p className="font-semibold text-foreground">Sélectionnez une conversation</p>
                            <p className="text-sm text-muted-foreground mt-1">
                                Choisissez une conversation dans la liste à gauche pour commencer
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MessagesBooking;
